import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

// ── Types ──────────────────────────────────────────────────────────────────

export interface PrintLogEntry {
  id: string;
  org_id: string;
  label_id: string | null;
  label_name: string;
  size: string;
  quantity: number;
  printed_by: string | null;
  printed_at: string;
}

export interface PrintStatsSummary {
  lastDate: string;   // ISO string of most recent print
  lastQty:  number;
  total:    number;   // cumulative labels printed for this label
}

// ── Helpers ────────────────────────────────────────────────────────────────

async function getOrgAndUser() {
  const { data: { user } } = await supabase.auth.getUser();
  const { data } = await supabase
    .from("user_account")
    .select("org_id")
    .eq("external_auth_user", user!.id)
    .single();
  return { userId: user!.id, orgId: data!.org_id as string };
}

// ── Record a print job (standalone, called after successful print) ──────────

export async function recordPrint(input: {
  label_id?: string | null;
  label_name: string;
  size: string;
  quantity: number;
}): Promise<void> {
  try {
    const { userId, orgId } = await getOrgAndUser();
    const { error } = await supabase.from("case_label_print_log" as any).insert({
      org_id:     orgId,
      label_id:   input.label_id ?? null,
      label_name: input.label_name,
      size:       input.size,
      quantity:   input.quantity,
      printed_by: userId,
      printed_at: new Date().toISOString(),
    });
    if (error) console.error("Print log insert failed:", error.message);
  } catch (e) {
    console.error("Print log error:", e);
  }
}

// ── Query: history for one label ───────────────────────────────────────────

export function useLabelPrintLog(labelId: string | null | undefined) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["case-label-print-log", labelId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("case_label_print_log" as any)
        .select("*")
        .eq("label_id", labelId!)
        .order("printed_at", { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as PrintLogEntry[];
    },
    enabled: !!labelId && isAuthenticated && !authLoading,
  });
}

// ── Query: per-label stats for the sidebar ────────────────────────────────

export function useAllPrintStats() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["case-label-print-log-stats"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("case_label_print_log" as any)
        .select("label_id, printed_at, quantity")
        .not("label_id", "is", null)
        .order("printed_at", { ascending: false });
      if (error) throw error;

      // Aggregate per label_id: first row = most recent (sorted DESC above)
      const map = new Map<string, PrintStatsSummary>();
      for (const row of (data ?? []) as Pick<PrintLogEntry, "label_id" | "printed_at" | "quantity">[]) {
        const lid = row.label_id as string;
        if (!map.has(lid)) {
          map.set(lid, { lastDate: row.printed_at, lastQty: row.quantity, total: 0 });
        }
        map.get(lid)!.total += row.quantity;
      }
      return map;
    },
    enabled: isAuthenticated && !authLoading,
    staleTime: 30_000,
  });
}

// ── Query: all print jobs across all labels (consolidated history view) ────

export function useAllPrintLog() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["case-label-print-log-all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("case_label_print_log" as any)
        .select("*")
        .order("printed_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as PrintLogEntry[];
    },
    enabled: isAuthenticated && !authLoading,
    staleTime: 30_000,
  });
}

// ── Invalidation helper (call after recordPrint) ──────────────────────────

export function useInvalidatePrintLog() {
  const qc = useQueryClient();
  return (labelId?: string | null) => {
    qc.invalidateQueries({ queryKey: ["case-label-print-log"] });
    qc.invalidateQueries({ queryKey: ["case-label-print-log-stats"] });
    qc.invalidateQueries({ queryKey: ["case-label-print-log-all"] });
    if (labelId) {
      qc.invalidateQueries({ queryKey: ["case-label-print-log", labelId] });
    }
  };
}
